import { useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Checkbox,
  TextField,
  Box,
  Alert,
} from "@mui/material";
import localize from "../../../common/localize";
import ColorPicker from "../../../components/ColorPicker";
import { getInputProps, getInputLabelProps } from "../../../common/appStyles";
import Combobox from "../../../components/common/Combobox";
import { getOperationalStatusNameFromId } from "../../../common/utils";
import { getNotificationTypes } from "../../../common/EntitiesManagement";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import NotificationSetup from "../../../components/common/NotificationSetup";
import ExpandableView from "../../../components/common/ExpandableView";

export default function AddOrEditEventStatus({ model, setModel }) {
  const handleNotificationTypeChange = (value) => {
    if (!value) {
      setModel({
        ...model,
        notificationType: 1,
        notificationTypeSelected: getNotificationTypes()[0],
      });
      return;
    }

    setModel({
      ...model,
      notificationType: value.Id,
      notificationTypeSelected: value,
    });
  };

  const handleChange = (e) => {
    setModel({
      ...model,
      [e.target.name]:
        e.target.name === "IsInitial" ||
        e.target.name === "IsFinal" ||
        e.target.name === "notifyByEmail"
          ? e.target.checked
          : e.target.value,
    });
  };

  const handleOperationalStatusChange = (value) => {
    if (!value) {
      setModel({
        ...model,
        OperationalStatus: 1,
        operationalStatusModel: {
          Id: 1,
          Name: getOperationalStatusNameFromId(1),
        },
      });
      return;
    }

    setModel({
      ...model,
      OperationalStatus: value.Id,
      operationalStatusModel: value,
    });
  };

  return (
    <div className="w-full">
      <ExpandableView
        components={[
          {
            Name: localize.general_information,
            Component: (
              <form className="w-full">
                <div className="w-full flex flex-wrap my-4">
                  <div className="w-full">
                    <TextField
                      required
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      size="small"
                      fullWidth
                      id="outlined-basic"
                      name="Name"
                      label={localize.name}
                      variant="outlined"
                      onChange={handleChange}
                      value={model.Name}
                    />
                  </div>
                </div>
                {/* operational status */}
                <div className="w-full flex flex-wrap my-4">
                  <div className="w-full">
                    <FormControl required fullWidth>
                      <Combobox
                        isRequired
                        id="OperationalStatusId"
                        fieldLabel={localize.operational_status}
                        fieldOptions={[
                          { Id: 1, Name: getOperationalStatusNameFromId(1) },
                          { Id: 2, Name: getOperationalStatusNameFromId(2) },
                          { Id: 3, Name: getOperationalStatusNameFromId(3) },
                          { Id: 4, Name: getOperationalStatusNameFromId(4) },
                          { Id: 5, Name: getOperationalStatusNameFromId(5) },
                        ]}
                        fieldValue={model.operationalStatusModel}
                        handleValueChange={handleOperationalStatusChange}
                      />
                    </FormControl>
                  </div>
                </div>
                {/* color pickers */}
                <Box className="flex flex-col gap-2 w-full">
                  <Alert severity="info" className="w-full">
                    <p className="text-xs">{localize.colors_note}</p>
                  </Alert>
                  <Box className="flex justify-between gap-2 mt-2">
                    <Box>
                      <ColorPicker
                        id="bg-color-picker"
                        value={model.BackgroundColor}
                        name="BackgroundColor"
                        onChange={handleChange}
                        labelText={localize.background_color}
                      />
                    </Box>

                    <Box>
                      <ColorPicker
                        id="fg-color-picker"
                        value={model.ForegroundColor}
                        name="ForegroundColor"
                        onChange={handleChange}
                        labelText={localize.foreground_color}
                      />
                    </Box>
                  </Box>
                </Box>
                <div className="flex flex-wrap mb-4"></div>
                {/* Checkboxes */}
                <Box className="flex flex-col gap-4">
                  <Box className="w-full">
                    <FormControlLabel
                      size="small"
                      name="IsInitial"
                      control={
                        <Checkbox
                          size="small"
                          name="IsInitial"
                          checked={model.IsInitial}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <span style={{ fontSize: "0.8em" }}>
                          <PageTitleSubtitle
                            useEventTitleStyle
                            useFieldTitleStyle
                            title={localize.is_initial_status}
                            subtitle={localize.initial_status_note}
                          />
                        </span>
                      }
                    />
                  </Box>

                  <Box className="w-full">
                    <FormControlLabel
                      size="small"
                      name="IsFinal"
                      control={
                        <Checkbox
                          size="small"
                          name="IsFinal"
                          checked={model.IsFinal}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <span style={{ fontSize: "0.8em" }}>
                          <PageTitleSubtitle
                            useEventTitleStyle
                            useFieldTitleStyle
                            title={localize.is_final_status}
                            subtitle={localize.final_status_note}
                          />
                        </span>
                      }
                    />
                  </Box>
                </Box>
              </form>
            ),
          },
          {
            Name: localize.notification,
            Component: (
              <div className="flex flex-col w-full gap-2">
                <div className="w-full my-2">
                  <FormControlLabel
                    size="small"
                    name="notifyByEmail"
                    control={
                      <Checkbox
                        size="small"
                        name="notifyByEmail"
                        checked={model.notifyByEmail}
                        onChange={handleChange}
                      />
                    }
                    label={
                      <span style={{ fontSize: "0.8em" }}>
                        <PageTitleSubtitle
                          title={localize.email_notification}
                          subtitle={localize.notity_by_email_status_change}
                          useEventTitleStyle
                          useFieldTitleStyle
                        />
                      </span>
                    }
                  />
                </div>
                {model?.notifyByEmail && (
                  <div className="w-full my-2">
                    <FormControl required fullWidth>
                      <Combobox
                        isRequired
                        disabled={!model.notifyByEmail}
                        id="notificationType"
                        fieldLabel={localize.notify_when}
                        fieldOptions={getNotificationTypes() ?? []}
                        fieldValue={model.notificationTypeSelected ?? null}
                        handleValueChange={handleNotificationTypeChange}
                      />
                    </FormControl>
                  </div>
                )}

                {model?.notifyByEmail && (
                  <div className="flex flex-wrap my-2 w-full">
                    <Alert severity="info" className="w-full">
                      <p className="text-xs">{localize.emails_note}</p>
                    </Alert>
                  </div>
                )}

                {model?.notifyByEmail && (
                  <div className="w-full flex flex-wrap">
                    <div className="w-full">
                      <TextField
                        required
                        InputLabelProps={getInputLabelProps()}
                        inputProps={getInputProps()}
                        size="small"
                        fullWidth
                        multiline
                        rows={4}
                        id="outlined-basic"
                        name="emails"
                        label={localize.emails}
                        variant="outlined"
                        onChange={handleChange}
                        value={model.emails ?? ""}
                        disabled={!model.notifyByEmail}
                      />
                    </div>
                  </div>
                )}

                <NotificationSetup
                  entity={model}
                  setEntity={setModel}
                  subtitle={localize.notify_status_change}
                  showOnlyDefinedToUser
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
